// Crypto API Cache Service
// Prevents duplicate API calls and saves credits

interface CachedData {
  data: any;
  timestamp: number;
  params: string;
}

class CryptoCache {
  private cache: Map<string, CachedData> = new Map();
  private readonly CACHE_DURATION = 120000; // 2 minutes cache for listings
  private readonly QUOTE_CACHE_DURATION = 60000; // 1 minute cache for quotes (prices change frequently)
  private pendingRequests: Map<string, Promise<any>> = new Map();

  // Generate cache key from params
  private getCacheKey(params: any): string {
    return JSON.stringify(params || {});
  }

  // Check if cached data is still valid
  private isValid(cached: CachedData, cacheType: 'listing' | 'quote' = 'listing'): boolean {
    const now = Date.now();
    const duration = cacheType === 'quote' ? this.QUOTE_CACHE_DURATION : this.CACHE_DURATION;
    return (now - cached.timestamp) < duration;
  }
  
  // Get cached quote data if valid
  getQuote(symbol: string): any | null {
    const key = `quote_${symbol.toUpperCase()}`;
    const cached = this.cache.get(key);
    
    if (cached && this.isValid(cached, 'quote')) {
      // console.log(`📦 Using cached quote for ${symbol}`); // Development only
      return cached.data;
    }
    
    return null;
  }
  
  // Set cached quote data
  setQuote(symbol: string, data: any): void {
    const key = `quote_${symbol.toUpperCase()}`;
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      params: key
    });
    // console.log(`💾 Cached quote for ${symbol}`); // Development only
  }
  
  // Check if quote request is pending
  hasPendingQuote(symbol: string): boolean {
    const key = `quote_${symbol.toUpperCase()}`;
    return this.pendingRequests.has(key);
  }
  
  // Get pending quote request
  getPendingQuote(symbol: string): Promise<any> | null {
    const key = `quote_${symbol.toUpperCase()}`;
    return this.pendingRequests.get(key) || null;
  }
  
  // Set pending quote request
  setPendingQuote(symbol: string, promise: Promise<any>): void {
    const key = `quote_${symbol.toUpperCase()}`;
    this.pendingRequests.set(key, promise);
    
    // Remove from pending when resolved
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });
  }

  // Get cached data if valid
  get(params: any): any | null {
    const key = this.getCacheKey(params);
    const cached = this.cache.get(key);
    
    if (cached && this.isValid(cached)) {
      // console.log('📦 Using cached crypto data'); // Development only
      return cached.data;
    }
    
    return null;
  }

  // Set cache data
  set(params: any, data: any): void {
    const key = this.getCacheKey(params);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      params: key
    });
    // console.log('💾 Cached crypto data'); // Development only
  }

  // Check if request is already pending
  hasPendingRequest(params: any): boolean {
    const key = this.getCacheKey(params);
    return this.pendingRequests.has(key);
  }

  // Get pending request promise
  getPendingRequest(params: any): Promise<any> | null {
    const key = this.getCacheKey(params);
    return this.pendingRequests.get(key) || null;
  }

  // Set pending request
  setPendingRequest(params: any, promise: Promise<any>): void {
    const key = this.getCacheKey(params);
    this.pendingRequests.set(key, promise);
    
    // Remove from pending when resolved
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });
  }

  // Clear cache
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Clear expired cache entries
  clearExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    // Collect expired keys
    this.cache.forEach((cached, key) => {
      if ((now - cached.timestamp) >= this.CACHE_DURATION) {
        keysToDelete.push(key);
      }
    });
    
    // Delete expired entries
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

export const cryptoCache = new CryptoCache();

// Clear expired entries every 5 minutes
setInterval(() => {
  cryptoCache.clearExpired();
}, 300000);

